import { useEditorStore } from '../../store/editorStore'
import { useUiStore, DEFAULT_PANEL_SIZES } from '../../store/uiStore'
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts'
import { LoadingState, ErrorState } from '../common/States'
import { ResizeHandle } from '../common/ResizeHandle'
import { Header } from './Header'
import { AssetBin } from './AssetBin/AssetBin'
import { PreviewPanel } from './Preview/PreviewPanel'
import { ControlPanel } from './ControlPanel/ControlPanel'
import { Timeline } from './Timeline/Timeline'
import { JobToast } from './JobToast'

export function EditorScreen() {
  useKeyboardShortcuts()
  const editorLoading = useEditorStore((s) => s.editorLoading)
  const editorError = useEditorStore((s) => s.editorError)
  const currentProject = useEditorStore((s) => s.currentProject)
  const openProject = useEditorStore((s) => s.openProject)
  const panelSizes = useUiStore((s) => s.panelSizes)
  const setPanelSize = useUiStore((s) => s.setPanelSize)

  if (editorLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-ink-bg">
        <LoadingState label="プロジェクトを読み込み中…" />
      </div>
    )
  }
  if (editorError || !currentProject) {
    return (
      <div className="h-screen flex items-center justify-center bg-ink-bg">
        <ErrorState
          message={editorError ?? 'プロジェクトが見つかりません'}
          onRetry={currentProject ? () => openProject(currentProject.id) : undefined}
        />
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-ink-bg text-text min-w-[1280px]">
      <Header />
      <div className="flex flex-1 min-h-0" data-testid="layout-main-row">
        <AssetBin />
        <ResizeHandle
          axis="x"
          data-testid="layout-resize-left"
          getSize={() => useUiStore.getState().panelSizes.left}
          setSize={(px) => setPanelSize('left', px)}
          onDoubleReset={() => setPanelSize('left', DEFAULT_PANEL_SIZES.left)}
        />
        <PreviewPanel />
        <ResizeHandle
          axis="x"
          invert
          data-testid="layout-resize-right"
          getSize={() => useUiStore.getState().panelSizes.right}
          setSize={(px) => setPanelSize('right', px)}
          onDoubleReset={() => setPanelSize('right', DEFAULT_PANEL_SIZES.right)}
        />
        {/*
          UI_MK2_PHASE2.md §2-7 (old-comment fixup): ControlPanel.tsx is out
          of EditorScreen's ownership (UI_MK2_PHASE1.md §1 → §1 2b in the
          second stage) and, as of the second stage, is being restructured
          into the "selection-driven" panel described in the reference doc
          §6. This wrapper still exists to give the right-hand resize handle
          a real, working boundary (drag/persist/double-click-reset all take
          effect on `panelSizes.right`) regardless of ControlPanel's own
          internal width handling — if ControlPanel's root className isn't
          `w-full`, its content stays visually fixed (clipped or gapped)
          inside this box.
        */}
        <div style={{ width: panelSizes.right }} className="shrink-0 overflow-hidden flex flex-col min-h-0">
          <ControlPanel />
        </div>
      </div>
      <ResizeHandle
        axis="y"
        invert
        data-testid="layout-resize-timeline"
        getSize={() => useUiStore.getState().panelSizes.timeline}
        setSize={(px) => setPanelSize('timeline', px)}
        onDoubleReset={() => setPanelSize('timeline', DEFAULT_PANEL_SIZES.timeline)}
      />
      {/*
        UI_MK2_PHASE2.md §2-7 (old-comment fixup): Timeline.tsx's root is
        `h-full` (with a `min-h-[260px]` fallback for when no explicit
        `height` is given — UI_MK2_PHASE1.md §5), so this fixed-height,
        overflow-hidden wrapper is what actually decides the rendered
        timeline height via `panelSizes.timeline`.
      */}
      <div style={{ height: panelSizes.timeline }} className="shrink-0 overflow-hidden" data-testid="layout-timeline-wrap">
        <Timeline />
      </div>
      <JobToast />
    </div>
  )
}
